import { AppVisibility } from 'quasar'
import store from 'src/assets/store'
import api from 'src/assets/api/gql'
import Subscription from 'src/assets/api/Subscription'
import MessengerSubscription from 'src/graphql/messenger/MessengerSubscription.gql'
import MessengerActivities from 'src/graphql/messenger/MessengerActivities.gql'
import MessengerActivityGet from 'src/graphql/messenger/MessengerActivityGet.gql'
import MessengerSend from 'src/graphql/messenger/MessengerSend.gql'
import MessengerRead from 'src/graphql/messenger/MessengerRead.gql'
import MessengerHistory from 'src/graphql/messenger/MessengerHistory.gql'
import { MessengerActivity } from 'src/graphql/types'

export default {

  activities: [] as MessengerActivity[],
  activitiesMap: new Map<string, MessengerActivity>(),
  activitiesLoadCursor: null as string | null,
  activitiesLoaded: false,

  chat: {
    activity: null as MessengerActivity | null,
    history: [] as Json[]
  },

  async activitiesLoad (done: (stop?: boolean) => void) {
    if (!this.activitiesLoaded) {
      const { data: { messengerActivities } } = await api.query({
        query: MessengerActivities,
        variables: {
          after: this.activitiesLoadCursor
        }
      })
      if (messengerActivities) {
        for (const activity of messengerActivities.nodes as MessengerActivity[]) {
          const key = `${activity.courseId}:${activity.studentId}:${activity.presenterId}`
          if (!this.activitiesMap.get(key)) {
            this.activitySetup(activity)
          }
        }
        if (!this.chat.activity) {
          this.activitiesSort()
        }
        this.activitiesLoadCursor = messengerActivities.pageInfo.hasNextPage ? messengerActivities.pageInfo.endCursor as string : null
      }
      this.activitiesLoaded = !this.activitiesLoadCursor
    }
    done(this.activitiesLoaded)
  },

  activitySetup (data: MessengerActivity): MessengerActivity {
    const key = `${data.courseId}:${data.studentId}:${data.presenterId}`
    if (!this.activitiesMap.get(key)) {
      this.activitiesMap.set(key, data)
    }
    const activity = this.activitiesMap.get(key)
    if (activity) {
      if (data.lastMessage) {
        activity.lastMessageSentAt = data.lastMessage.at as string
        if (activity === this.chat.activity) {
          if ((activity.lastMessage?.id || 0) < data.lastMessage.id)
          this.messageAppend(this.chat.history, data.lastMessage)
        }
      }
      Object.assign(activity, data)
      if (!activity.id) {
        this.activityReload(activity)
      }
      return activity
    }
    return activity || data
  },

  async activityRead (activity: MessengerActivity) {
    await api.mutate({
      query: MessengerRead,
      variables: {
        input: {
          courseId: activity.courseId,
          studentId: activity.studentId,
          presenterId: activity.presenterId
        }
      }
    })
  },

  async activityReload (activity: MessengerActivity) {
    const { data: { messengerActivityByCourseIdAndStudentIdAndPresenterId, students: { nodes: [student]} } } = await api.query({
      query: MessengerActivityGet,
      variables: {
        courseId: activity.courseId,
        studentId: activity.studentId,
        presenterId: activity.presenterId
      }
    })
    Object.assign(activity, messengerActivityByCourseIdAndStudentIdAndPresenterId, { student })
    if (!this.chat.activity) {
      this.activitiesSort()
    }
  },

  async activityLoadHistory (done: (stop?: boolean) => void) {
    if (this.chat.activity) {
      const { data: { messengerHistory } } = await api.query({
        query: MessengerHistory,
        variables: {
          courseId: this.chat.activity.courseId,
          studentId: this.chat.activity.studentId,
          presenterId: this.chat.activity.presenterId,
          before: this.chat.history.length ? this.chat.history[0].messages[0].id : 0,
          last: 100
        }
      })
      const history = messengerHistory || [] as Json[]
      for (const message of history.reverse()) {
        this.messagePrepend(this.chat.history, message)
      }
      if (this.chat.activity.unreadMessages && AppVisibility.appVisible) {
        this.activityRead(this.chat.activity)
      }
    }
    done(this.chat.history.length === 0 || this.chat.history[0].messages[0].id === 1)
  },

  activitiesSort () {
    this.activities = [...this.activitiesMap.values()].filter(value => value.id).sort((a, b) => {
      return +(a.lastMessageSentAt || 0) - +(b.lastMessageSentAt || 0)
    }).reverse()
  },

  async chatOpen (activity: MessengerActivity) {
    // @ts-ignore
    if (!activity.student) {
      this.activityReload(activity)
    }
    this.chat.activity = activity
  },

  chatClose () {
    this.chat.activity = null
    this.chat.history = []
    this.activitiesSort()
  },

  async send (message: string) {
    if (this.chat.activity) {
      await api.mutate({
        query: MessengerSend,
        variables: {
          input: {
            courseId: this.chat.activity.courseId,
            studentId: this.chat.activity.studentId,
            presenterId: this.chat.activity.presenterId,
            message
          }
        }
      })
    }
  },

  messagePrepend (history: Json[], message: Json) {
    const firstMessage = history.length ? history[ 0 ] : null
    if (firstMessage && firstMessage.sender.id === message.by) {
      if (firstMessage.messages[0].id !== message.id) {
        firstMessage.messages.unshift(message)
      }
    } else {
      history.unshift(this.createHistoryFirstMessage(message))
    }
  },

  messageAppend (history: Json[], message: Json) {
    const lastMessage = history.length ? history[ history.length - 1 ] : null
    if (lastMessage && lastMessage.sender.id === message.by) {
      if (lastMessage.messages[ lastMessage.messages.length - 1 ].id !== message.id) {
        lastMessage.messages.push(message)
      }
    } else {
      history.push(this.createHistoryFirstMessage(message))
    }
  },

  createHistoryFirstMessage (message: Json) {
    if (message.by === store.state.person.id) {
      return {
        sender: {
          id: message.by,
          name: store.state.person.name,
          avatarUrl: store.state.person.avatarUrl
        },
        messages: [message]
      }
    } else {
      return {
        sender: {
          id: message.by,
          name: this.chat.activity?.personPublicProfile?.name,
          avatarUrl: this.chat.activity?.personPublicProfile?.avatarUrl
        },
        messages: [message]
      }
    }
  },

  async start () {
    const subscription = new Subscription(MessengerSubscription)
    subscription.start((data: any) => {
      const init = data?.messenger?.init
      if (init) {
        // @todo: reset activities list and close messenger window
      }
      const incoming = data?.messenger?.messengerActivity || data?.messenger?.payload?.data
      if (incoming) {
        this.activitySetup(incoming)
        if (this.chat.activity) {
          if (incoming.unreadMessages && AppVisibility.appVisible) {
            this.activityRead(this.chat.activity)
          }
        } else {
          this.activitiesSort()
        }
      }
    })
  }
}
